import { useState, useEffect } from 'react';

import {
  IconUser,
  IconTrash,
  IconSchool,
  IconDevices,
  IconMoodKid,
  IconClockHour4,
} from '@tabler/icons-react';

import CustomModal from '@/components/CustomModal';
import DeviceDetailsSkeleton from '@/components/SkeletonLoader/DeviceDetailsSkeleton';
import { ERoleAccess } from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useFormatText } from '@/hooks/useFormatText';
import { getDeviceDetails } from '@/service/deviceServices';
import useCommonStore from '@/store/useCommonStore';
import { IDevice } from '@/types';
import { formatLastActive } from '@/utils';

interface IDeviceDetailsModalProps {
  setIsOpen: (open: boolean) => void;
  deviceId: string | undefined;
  onRefresh?: () => void;
  handleRemoveDevice?: () => void;
}

const DeviceDetailsModal = ({
  setIsOpen,
  deviceId,
  handleRemoveDevice,
}: IDeviceDetailsModalProps) => {
  const { hasAccess } = useAccess();

  const [device, setDevice] = useState<IDevice | null>(null);
  const { formatText } = useFormatText();
  const { loadingModal, setLoadingModal } = useCommonStore();

  useEffect(() => {
    const fetchDeviceDetails = async () => {
      if (!deviceId) return;
      setLoadingModal(true);
      try {
        const response = await getDeviceDetails(deviceId);
        if (response?.status) {
          setDevice(response?.data);
        }
      } catch (error) {
        console.error('Error fetching device details:', error);
      } finally {
        setLoadingModal(false);
      }
    };

    fetchDeviceDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  if (!hasAccess(ERoleAccess.DEVICE_MANAGEMENT)) return null;

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      isHeader
      headercontent={
        <h2 className="text-lg leading-7">
          {formatText('device-management.modal-details.title')}
        </h2>
      }
      position="right"
      isFooter
      cancelButtonProps={{
        label: formatText(
          'device-management.modal-details.action-remove-device'
        ),
        icon: <IconTrash size={16} />,
        className: '!text-red-600',
        desructive: true,
        onClick: () => handleRemoveDevice?.(),
        disabled: loadingModal,
      }}
      confirmButtonProps={{
        label: formatText('common.actions.done'),
        onClick: () => setIsOpen(false),
      }}
      loadingModal={loadingModal}
    >
      {loadingModal ? (
        <DeviceDetailsSkeleton />
      ) : (
        <div className="w-full bg-white space-y-10">
          {/* Device Header */}
          <div className="bg-brand-light-50 rounded-2xl p-6 space-y-6">
            <div className="flex gap-2 items-center">
              <div className="flex-1 flex items-center justify-between">
                <div className="flex gap-2 items-center">
                  <IconDevices
                    size={32}
                    className="text-white p-2 rounded-lg bg-brand-default"
                  />
                  <span className="text-neutral-primary text-lg font-semibold">
                    {formatText('device-management.modal-details.device-id')}
                  </span>
                </div>

                <span className="text-base font-semibold text-neutral-primary rounded bg-white p-1">
                  {device?.device_id ?? '-'}
                </span>
              </div>
            </div>

            <div className="flex justify-between items-center text-sm">
              <span className="text-neutral-secondary">
                {formatText('device-management.modal-details.device-model')}:
              </span>
              <span className="text-neutral-primary font-semibold uppercase">
                {device?.device_model ?? '-'}
              </span>
            </div>

            <div className="flex justify-between items-center text-sm">
              <span className="text-neutral-secondary">
                {formatText('device-management.modal-details.operating-system')}
                :
              </span>
              <span className="text-neutral-primary font-semibold">
                {device?.os_version ?? '-'}
              </span>
            </div>

            <div className="flex justify-between items-center text-sm">
              <span className="text-neutral-secondary">
                {formatText('device-management.modal-details.app-version')}:
              </span>
              <span className="text-neutral-primary font-semibold">
                {device?.app_version ?? '-'}
              </span>
            </div>

            <div className="flex justify-between items-center text-sm">
              <span className="text-neutral-secondary">
                {formatText('device-management.modal-details.device-type')}:
              </span>
              <span className="text-neutral-primary font-semibold">
                {device?.device_name ?? '-'}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-start gap-3">
              {/* Icon */}
              <div className="mt-0.5 text-neutral-hover">
                <IconMoodKid size={20} />
              </div>

              {/* Text */}
              <div className="flex flex-col gap-1">
                <span className="text-sm text-neutral-secondary leading-6">
                  {formatText('device-management.modal-details.linked-child')}
                </span>
                <span className="text-base font-medium text-neutral-primary">
                  {device?.linked_child ?? '-'}
                </span>
              </div>
            </div>
            <div className="flex items-start gap-3">
              {/* Icon */}
              <div className="mt-0.5 text-neutral-hover">
                {device?.linked_school ? (
                  <IconSchool size={20} />
                ) : (
                  <IconUser size={20} />
                )}
              </div>

              {/* Text */}
              <div className="flex flex-col gap-1">
                <span className="text-sm text-neutral-secondary leading-6">
                  {device?.linked_school
                    ? formatText(
                        'device-management.modal-details.linked-school'
                      )
                    : formatText('device-management.column.associated-parent')}
                </span>
                <span className="text-base font-medium text-neutral-primary">
                  {device?.linked_school
                    ? device?.linked_school
                    : (device?.associated_parent ?? '-')}
                </span>
              </div>
            </div>
            <div className="flex items-start gap-3">
              {/* Icon */}
              <div className="mt-0.5 text-neutral-hover">
                <IconClockHour4 size={20} />
              </div>

              {/* Text */}
              <div className="flex flex-col gap-1">
                <span className="text-sm text-neutral-secondary leading-6">
                  {formatText('device-management.modal-details.last-active')}
                </span>
                <span className="text-base font-medium text-neutral-primary">
                  {device?.last_active
                    ? formatLastActive(device?.last_active, formatText)
                    : '-'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </CustomModal>
  );
};

export default DeviceDetailsModal;
